import type { RepeatingSectionRowData } from '@/features/AI-documents/types/docGen.types';

export interface WithdrawDirectorFormModel {
  companyName: string;
  companyRegistrationNo: string;
  companyAddress: string;
  resignDirectorName: string;
  resignDirectorAddress: string;
  resignationDate: string;
  reasonForCessation: string;
  otherReasonForCessation: string;
  secretaryFirmName: string;
  resolutionDate: string;
  repeatingData: Record<string, RepeatingSectionRowData[]>;
  contactCd: string;
  positionCd: string;
  contactData: Record<string, any> | null;
}

export function createEmptyWithdrawDirectorForm(): WithdrawDirectorFormModel {
  return {
    companyName: '',
    companyRegistrationNo: '',
    companyAddress: '',
    resignDirectorName: '',
    resignDirectorAddress: '',
    resignationDate: '',
    reasonForCessation: '',
    otherReasonForCessation: '',
    secretaryFirmName: '',
    resolutionDate: '',
    repeatingData: {},
    contactCd: '',
    positionCd: 'B30027',
    contactData: null,
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** BizFile / ACRA cessation — Others(B12004) → ceasedReason `OTH` (OfficerCessationForm.getEFilingData 와 동일) */
function ceasedReasonCodeForEfiling(reasonCd: string): string {
  const r = trim(reasonCd);
  if (r === 'B12003') return 'RESIGN';
  if (r === 'B12004') return 'OTH';
  return '';
}

function normalizeRepeatingData(
  repeatingData: Record<string, RepeatingSectionRowData[]> | null | undefined,
): Record<string, RepeatingSectionRowData[]> {
  const out: Record<string, RepeatingSectionRowData[]> = {};
  if (!repeatingData || typeof repeatingData !== 'object') return out;

  for (const [sectionName, rows] of Object.entries(repeatingData)) {
    if (!Array.isArray(rows)) continue;
    const cleanRows = rows
      .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
      .map((row) => {
        const cleanRow: RepeatingSectionRowData = {};
        for (const [key, value] of Object.entries(row)) {
          cleanRow[key] = trim(value);
        }
        return cleanRow;
      });
    if (cleanRows.length > 0) out[sectionName] = cleanRows;
  }

  return out;
}

/**
 * DR (Withdraw Director), Resignation Letter (Director) 템플릿 [[...]] 변수.
 */
export function toTemplateVariables(model: WithdrawDirectorFormModel): Record<string, any> {
  const repeatingData = normalizeRepeatingData(model.repeatingData);
  return {
    company_name: trim(model.companyName),
    company_registration_no: trim(model.companyRegistrationNo),
    company_address: trim(model.companyAddress),
    resign_director_name: trim(model.resignDirectorName),
    resign_director_address: trim(model.resignDirectorAddress),
    resignation_date: trim(model.resignationDate),
    reason_for_cessation: trim(model.reasonForCessation),
    other_reason_for_cessation: trim(model.otherReasonForCessation),
    reasonForCessation: trim(model.reasonForCessation),
    ceased_reason: ceasedReasonCodeForEfiling(trim(model.reasonForCessation)),
    ceasedReason: ceasedReasonCodeForEfiling(trim(model.reasonForCessation)),
    otherReason: trim(model.otherReasonForCessation),
    secretary_firm_name: trim(model.secretaryFirmName),
    resolution_date: trim(model.resolutionDate),
    repeatingData,
    ...repeatingData,
    /** 템플릿·피커 키 호환 (대소문자) — 빈 값이면 상세에서 서명자 입력 유도 */
    signers: '',
    Signers: '',
  };
}

export function isWithdrawDirectorFormComplete(model: WithdrawDirectorFormModel): boolean {
  const baseRequired = [
    model.companyName,
    model.companyRegistrationNo,
    model.companyAddress,
    model.resignDirectorName,
    model.resignDirectorAddress,
    model.resignationDate,
    model.reasonForCessation,
    model.secretaryFirmName,
    model.resolutionDate,
  ].every((v) => trim(v).length > 0);

  if (!baseRequired) return false;

  if (trim(model.reasonForCessation) === 'B12004') {
    return trim(model.otherReasonForCessation).length > 0;
  }

  return true;
}

export function buildWithdrawDirectorTaskFormData(
  model: WithdrawDirectorFormModel,
  entityCd: string,
): Record<string, any> {
  const contactData = model.contactData ? { ...model.contactData } : null;
  const contactCd = trim(model.contactCd) || trim(contactData?.contactCd);
  const cessationDate = trim(model.resignationDate);
  const officerName =
    trim(model.resignDirectorName) ||
    trim(contactData?.contactName);

  return {
    entity: trim(entityCd),
    entityCd: trim(entityCd),
    entityName: { value: trim(model.companyName) },
    entityRegNo: { value: trim(model.companyRegistrationNo) },
    companyName: trim(model.companyName),
    companyRegistrationNo: trim(model.companyRegistrationNo),
    officerNameCd: contactCd,
    officerCode: contactCd,
    officerName,
    contactCd,
    position: trim(model.positionCd) || 'B30027',
    positionCd: trim(model.positionCd) || 'B30027',
    positionText: 'Director',
    dateOfCessation: cessationDate,
    cessationDate,
    resignationDate: cessationDate,
    reasonForCessation: trim(model.reasonForCessation),
    ceasedReason: ceasedReasonCodeForEfiling(trim(model.reasonForCessation)),
    otherReason: trim(model.otherReasonForCessation),
    otherReasonForCessation: trim(model.otherReasonForCessation),
    resignDirectorName: trim(model.resignDirectorName),
    resignDirectorAddress: trim(model.resignDirectorAddress),
    companyAddress: trim(model.companyAddress),
    resolutionDate: trim(model.resolutionDate),
    contactData,
  };
}
